import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export type BatchActionType = 'parse' | 'ingest' | 'move';

@Component({
  selector: 'app-batch-action-confirm-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div class="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">

        <div class="flex items-start gap-3 mb-4">
          <div class="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center" [class]="iconBg">
            @if (action === 'move') {
              <svg class="w-5 h-5" [class]="iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            } @else {
              <svg class="w-5 h-5" [class]="iconColor" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            }
          </div>
          <div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ count }} file{{ count !== 1 ? 's' : '' }} selected</p>
          </div>
        </div>

        <p class="text-sm text-gray-600 dark:text-gray-400 mb-5 leading-relaxed">{{ description }}</p>

        @if (action === 'move') {
          <div class="mb-5">
            <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Destination path</label>
            <input
              type="text"
              [(ngModel)]="destPath"
              placeholder="folder/subfolder/"
              class="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        }

        <div class="flex justify-end gap-2">
          <button
            (click)="cancel.emit()"
            class="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            Cancel
          </button>
          <button
            (click)="confirmed.emit(destPath)"
            [disabled]="action === 'move' && !destPath.trim()"
            class="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium transition-colors">
            {{ confirmLabel }}
          </button>
        </div>
      </div>
    </div>
  `
})
export class BatchActionConfirmDialogComponent {
  @Input() action: BatchActionType = 'ingest';
  @Input() count = 1;
  @Output() confirmed = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<void>();

  destPath = '';

  get title(): string {
    const labels: Record<BatchActionType, string> = {
      parse: `Parse ${this.count} file${this.count !== 1 ? 's' : ''}`,
      ingest: `Ingest ${this.count} file${this.count !== 1 ? 's' : ''}`,
      move: `Move ${this.count} file${this.count !== 1 ? 's' : ''}`,
    };
    return labels[this.action];
  }

  get description(): string {
    const descs: Record<BatchActionType, string> = {
      parse: `Send ${this.count === 1 ? 'this file' : 'these files'} for text extraction. Results will be stored in the parsed bucket.`,
      ingest: `Index ${this.count === 1 ? 'this file' : 'these files'} into the Knowledge Graph.`,
      move: `Enter the destination folder path. Files will be moved there.`,
    };
    return descs[this.action];
  }

  get confirmLabel(): string {
    return { parse: 'Parse', ingest: 'Ingest', move: 'Move' }[this.action];
  }

  get iconBg(): string {
    return this.action === 'move'
      ? 'bg-gray-100 dark:bg-gray-800'
      : 'bg-blue-100 dark:bg-blue-900/30';
  }

  get iconColor(): string {
    return this.action === 'move'
      ? 'text-gray-600 dark:text-gray-400'
      : 'text-blue-600 dark:text-blue-400';
  }
}
